import { useMutation } from '@northladder/services';
import {
  ErrorEventsEnum,
  errorLogToRemoteUtil,
  IApiError,
} from '@northladder/utilities';

import { ISendUserLoginOPTData } from '../types';
import { USER_ACCOUNTS_API } from '../userAccountsAPIClient';

/**
 * This is to track the login type mutation keys in react query cache.
 */
const MUT_SEND_OTP_TYPE_KEY = 'send-otp-mutation-key';

interface IUseLoginOptions {
  onErrorCallback?: (err: IApiError) => void;
  /**
   * Callback to be by the calling app if the login is successful.
   * Note: Usually this would be best handled within the onSuccess of the hook
   * itself but since the login would require additional platform specific
   * features like `next/router` & `localStorage` on the web and
   * `react-navigation` & `asyncStorage` on mobile, this will be done in the
   * callback of the respective platform without having to embed them in the
   * util.
   */
  onSuccessCallback: (loginData: ISendUserLoginOPTData) => void;
}

/**
 * This performs a send otp request and retrieves the status and otp key.
 * Provides data fetching states and a callback to be used in the redirection
 * later.
 */
export const useSendOtpMutation = ({
  onErrorCallback,
  onSuccessCallback,
}: IUseLoginOptions) => {
  const { mutate, isPending, error, data } = useMutation({
    mutationKey: [MUT_SEND_OTP_TYPE_KEY],
    networkMode: 'always', // Even make calls when offline
    retry: false, // For login Request, do not retry failed requests.
    mutationFn: USER_ACCOUNTS_API.sendUserLoginOTP,
    onSuccess: onSuccessCallback,
    onError: (err: IApiError) => {
      errorLogToRemoteUtil({
        error,
        errorCode: ErrorEventsEnum.ERROR_IN_API_CALL,
        errorTitle: 'Error in Sending OTP Attempt',
        message: error?.message,
      });

      onErrorCallback?.(err);

      return err;
    },
  });

  return {
    data,
    error,
    isPending,
    sendOtp: mutate,
  };
};
